// ─────────────────────────────────────────────────────────────────────────────
// Gestión de usuarios (Admin General, a nivel de Jobs) — correos personales y verificación de las cuentas
// Frontline. Se construyó sobre la maqueta `entrega-gestion-usuarios`, con los componentes del panel
// (AdminSection, tabla "glass", StateBlock, Modal) para verse como el resto del admin.
//
// Lo que NO está aquí a propósito, porque se descartó: «Cargar Excel», «Descargar plantilla» y «No enviar».
// Tampoco la contraseña temporal: es la fase 2.
//
// El listado, los filtros, los contadores y la paginación son del SERVIDOR: el padrón Frontline son decenas de
// miles de fichas y la maqueta resolvía eso en el navegador sobre 14 filas de ejemplo. Los cuatro contadores
// cuentan el conjunto que dejan los filtros (no la página) y no aplican el filtro de verificación.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { frontlineUsersAdminApi } from '@/api/client';
import {
  FRONTLINE_SEGMENTS, FRONTLINE_VERIFICATION_STATES,
  type FrontlineSegment, type FrontlineUserAdminDto, type FrontlineUsersFilters, type FrontlineVerificationState,
} from '@/api/types';
import { InitialsAvatar, Toast, useToast } from '@/components/common';
import { useOUItems } from '@/hooks/useOUs';
import { useCurrentUser, type UserRole } from '@/hooks/useCurrentUser';
import { scopedOuItems } from './scopedOuItems';
import { useSites, useSsoDomains } from '@/api/recognition';
import { useFrontlineUsers, useSendFrontlineVerification } from '@/hooks/useFrontlineUsersAdmin';
import { AdminSection } from '@/views/recognition/admin/adminShared';
import { StateBlock } from '@/views/recognition/components';
import { AdminBulkUsers } from '@/views/recognition/admin/AdminBulkUsers';
import { descargarBlob } from '@/utils/blobDownload';
import { EditEmailModal } from './EditEmailModal';
import { VerificationModal } from './VerificationModal';
import { accesoPorSso, personalEsCorporativo } from './correosFicha';
import { frontlineErrorKey } from './frontlineErrors';
import '@/views/recognition/recognition.css';
import './GestionUsuariosTab.css';

/** Tamaños de página de la maqueta. El servidor acepta hasta 100, pero «todas» no existe aquí. */
const PAGE_SIZES = [10, 25, 50] as const;
const SEARCH_DEBOUNCE_MS = 300;
const FILTER_SELECT = 'adm-filter-select';
const PADRON_LOADER_ROLES: readonly UserRole[] = ['AdminGlobal', 'AdminBrillamos'];

/** Retrasa un valor: la búsqueda pide al servidor al dejar de teclear, no en cada letra. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

type Modal = { kind: 'edit' | 'verification'; user: FrontlineUserAdminDto } | null;

export function GestionUsuariosTab() {
  const { t, i18n } = useTranslation();
  const { t: tr } = useTranslation('recognition');
  const { toast, showSuccess, showError } = useToast();

  const [query, setQuery] = useState('');
  // null = la persona no ha elegido OU: manda el valor por omisión (la OU del alcance si es una sola, si no «todas»).
  const [pickedOuId, setPickedOuId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState('');
  const [segment, setSegment] = useState<FrontlineSegment | ''>('');
  const [state, setState] = useState<FrontlineVerificationState | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [modal, setModal] = useState<Modal>(null);
  const [exporting, setExporting] = useState(false);

  const search = useDebounced(query, SEARCH_DEBOUNCE_MS);
  const { data: allOuItems = [] } = useOUItems();
  const currentAdmin = useCurrentUser();
  const ouItems = scopedOuItems(allOuItems, currentAdmin);
  // La carga del padrón (Europa, USA y Central) la ven Admin General y Admin de Reconocimiento: son los que cargan
  // cualquier región. Los administradores de OU solo pueden cargar la región que cae en su OU, así que no se les ofrece
  // un botón que casi siempre acabaría en un rechazo de permiso (el servidor sigue acotando por alcance).
  const canLoadPadron = !!currentAdmin?.roles.some(role => PADRON_LOADER_ROLES.includes(role));
  // Un Admin de OU con una sola OU en su alcance entra con ese filtro ya puesto (sin «Todas», que ahí no significa nada).
  const singleOuId = ouItems.length === 1 ? ouItems[0].id : null;
  const scopedToSome = allOuItems.length > 0 && ouItems.length < allOuItems.length;
  const ouId = pickedOuId ?? singleOuId ?? '';

  const filters = useMemo<FrontlineUsersFilters>(() => ({
    search: search || undefined,
    ouId: ouId || undefined,
    siteId: siteId || undefined,
    segment: segment || undefined,
    state: state || undefined,
  }), [search, ouId, siteId, segment, state]);

  // Cualquier cambio de filtro vuelve a la primera página (como la maqueta).
  useEffect(() => { setPage(1); }, [filters, pageSize]);

  const list = useFrontlineUsers(filters, page, pageSize);
  const { data: sites = [] } = useSites(ouId ? [ouId] : []);
  const ssoDomains = useSsoDomains();
  const send = useSendFrontlineVerification();

  const data = list.data;
  const rows = data?.items ?? [];
  const summary = data?.summary;

  const sendButtonTitle = (u: FrontlineUserAdminDto): string => {
    if (u.estado === 'DelDirectorio') return t('frontlineUsers.err.directory');
    if (accesoPorSso(u, ssoDomains.data)) return t('frontlineUsers.err.sso');
    if (!u.correoPersonal) return t('frontlineUsers.noEmailTitle');
    if (u.estado === 'Verificado') return t('frontlineUsers.verifiedTitle');
    return t('frontlineUsers.sendTitle');
  };

  const formatShort = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });

  const onSend = async (user: FrontlineUserAdminDto) => {
    try {
      const result = await send.mutateAsync(user.id);
      showSuccess(t('frontlineUsers.sentOk', { email: result.enviadoA }));
    } catch (err) {
      showError(t(frontlineErrorKey(err)));
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const { blob, truncated } = await frontlineUsersAdminApi.exportCsv(filters);
      descargarBlob(blob, 'usuarios.csv');
      if (truncated) showError(t('frontlineUsers.exportTruncated'));
    } catch {
      showError(t('frontlineUsers.exportError'));
    } finally {
      setExporting(false);
    }
  };

  // El modal muestra la fila ACTUAL: tras enviar o verificar, el listado se refresca y la copia que se abrió quedaría vieja.
  const currentUser = modal ? (rows.find(r => r.id === modal.user.id) ?? modal.user) : null;

  const total = data?.totalCount ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pages);
  const start = (safePage - 1) * pageSize;
  const end = Math.min(start + pageSize, total);

  return (
    <>
    <AdminSection title={t('frontlineUsers.title')} description={t('frontlineUsers.subtitle')}>
      <div className="gu-panel">
        <div className="gu-header">
          <h3>{t('frontlineUsers.tableTitle')}</h3>
          <button type="button" className="btn btn--primary" onClick={() => void onExport()} disabled={exporting}>
            <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-download'}`} />{' '}
            {exporting ? t('frontlineUsers.exporting') : t('frontlineUsers.export')}
          </button>
        </div>

        <div className="gu-notice">
          <i className="fas fa-info-circle" aria-hidden="true" />
          <span>{t('frontlineUsers.notice')}</span>
        </div>

        <div className="adm-filter-bar" role="search">
          <div className="adm-filter-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('frontlineUsers.search')}
              aria-label={t('frontlineUsers.search')}
              maxLength={100}
            />
          </div>
          <select className={FILTER_SELECT} value={ouId} aria-label={t('frontlineUsers.colOu')}
            onChange={e => { setPickedOuId(e.target.value); setSiteId(''); }}>
            {singleOuId === null && <option value="">{scopedToSome ? t('frontlineUsers.ouAllMine') : t('frontlineUsers.ouAll')}</option>}
            {ouItems.map(ou => <option key={ou.id} value={ou.id}>{ou.name}</option>)}
          </select>
          <select className={FILTER_SELECT} value={siteId} aria-label={t('frontlineUsers.colSite')}
            onChange={e => setSiteId(e.target.value)}>
            <option value="">{t('frontlineUsers.siteAll')}</option>
            {sites.map(site => <option key={site.id} value={site.id}>{site.name}</option>)}
          </select>
          <select className={FILTER_SELECT} value={segment} aria-label={t('frontlineUsers.segment')}
            onChange={e => {
              setSegment(e.target.value as FrontlineSegment | '');
              // Cambiar el Tipo parte el padrón en otro conjunto: un estado de Verificación elegido antes («Del AD»,
              // «Verificado») ya no corresponde y mezclaba Staff con Frontline. Se limpia en vez de dejarlo oculto.
              setState('');
            }}>
            <option value="">{t('frontlineUsers.segmentAll')}</option>
            {FRONTLINE_SEGMENTS.map(s => <option key={s} value={s}>{t(`frontlineUsers.segmentOption.${s}`)}</option>)}
          </select>
          {/* Los estados de verificación son del padrón Frontline: con Staff no aplican (se deshabilita) y, con Frontline,
              «Del AD» se quita porque quien viene del directorio es personal de la empresa. */}
          <select className={FILTER_SELECT} value={state} aria-label={t('frontlineUsers.colState')}
            disabled={segment === 'Staff'}
            onChange={e => setState(e.target.value as FrontlineVerificationState | '')}>
            <option value="">{t('frontlineUsers.stateAll')}</option>
            {FRONTLINE_VERIFICATION_STATES
              .filter(s => !(segment === 'Frontline' && s === 'DelDirectorio'))
              .map(s => <option key={s} value={s}>{t(`frontlineUsers.state.${s}`)}</option>)}
          </select>
        </div>

        {summary && (
          <div className="gu-chips" aria-live="polite">
            <span className="gu-chip gu-st--Verificado">{t('frontlineUsers.chipVerified', { count: summary.verificados })}</span>
            <span className="gu-chip gu-st--Pendiente">{t('frontlineUsers.chipPending', { count: summary.pendientes })}</span>
            <span className="gu-chip gu-st--NoEnviado">{t('frontlineUsers.chipNotSent', { count: summary.noEnviados })}</span>
            <span className="gu-chip gu-st--SinCorreo">{t('frontlineUsers.chipNoEmail', { count: summary.sinCorreo })}</span>
            <span className="gu-chip gu-st--DelDirectorio">{t('frontlineUsers.chipDirectory', { count: summary.delDirectorio })}</span>
          </div>
        )}

        <StateBlock
          isLoading={list.isLoading}
          isError={list.isError}
          error={list.error}
          isEmpty={rows.length === 0}
          emptyText={t('frontlineUsers.empty')}
          onRetry={() => { void list.refetch(); }}
        >
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--glass gu-table">
              <thead>
                <tr>
                  <th>{t('frontlineUsers.colPayroll')}</th>
                  <th>{t('frontlineUsers.colName')}</th>
                  <th>{t('frontlineUsers.colCorp')}</th>
                  <th>{t('frontlineUsers.colPersonal')}</th>
                  <th>{t('frontlineUsers.colOu')}</th>
                  <th>{t('frontlineUsers.colSite')}</th>
                  <th>{t('frontlineUsers.colStatus')}</th>
                  <th className="gu-c">{t('frontlineUsers.colAction')}</th>
                  <th className="gu-c">{t('frontlineUsers.colSend')}</th>
                  <th className="gu-c">{t('frontlineUsers.colState')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u, index) => {
                  // Las de SSO entran con su cuenta corporativa: ni se les manda nada ni se les edita el correo (es su
                  // llave de identidad), y su correo —de trabajo— se muestra en la columna CORPORATIVA.
                  const delAd = u.estado === 'DelDirectorio';
                  const sendDisabled = delAd || !u.correoPersonal || u.estado === 'Verificado' || accesoPorSso(u, ssoDomains.data);
                  // Un correo de dominio corporativo guardado como correo de la ficha (padrón, registro con el correo de
                  // trabajo) va en la columna CORPORATIVA, no en la personal. Solo cambia dónde se pinta: enviar, editar y
                  // el historial siguen usando `correoPersonal`, que es el correo con el que la persona entra.
                  const personalCorp = personalEsCorporativo(u, ssoDomains.data);
                  const corporativo = delAd ? (u.correoCorporativo ?? u.correoPersonal)
                    : personalCorp ? (u.correoCorporativo || u.correoPersonal) : u.correoCorporativo;
                  const personal = delAd || personalCorp ? null : u.correoPersonal;
                  const sendTitle = sendButtonTitle(u);
                  const sentOn = u.estado === 'Pendiente' && u.ultimoEnvioVerificacion
                    ? t('frontlineUsers.sentOn', { date: formatShort(u.ultimoEnvioVerificacion) }) : null;
                  return (
                    <tr key={u.id}>
                      <td data-label={t('frontlineUsers.colPayroll')} className="gu-payroll">{u.nomina ?? '—'}</td>
                      <td data-label={t('frontlineUsers.colName')}>
                        <div className="adm-admin-cell">
                          <InitialsAvatar name={u.nombre || '?'} baseClass="rp-avatar" colorIndex={(page - 1) * pageSize + index} />
                          <strong>{u.nombre}</strong>
                        </div>
                      </td>
                      <td data-label={t('frontlineUsers.colCorp')} className="gu-mail">
                        {corporativo
                          ? <span className="gu-corp"><i className="fas fa-lock" aria-hidden="true" />{corporativo}</span>
                          : <span className="gu-empty">—</span>}
                        {u.sinLicencia && <span className="gu-tag gu-tag--nolicense">{t('frontlineUsers.noLicenseTag')}</span>}
                      </td>
                      <td data-label={t('frontlineUsers.colPersonal')} className="gu-mail">
                        {personal ?? <span className="gu-empty">—</span>}
                      </td>
                      <td data-label={t('frontlineUsers.colOu')}>{u.ou && <span className="gu-tag gu-tag--ou">{u.ou}</span>}</td>
                      <td data-label={t('frontlineUsers.colSite')}>{u.sitio && <span className="gu-tag gu-tag--site">{u.sitio}</span>}</td>
                      <td data-label={t('frontlineUsers.colStatus')}>
                        <span className={`gu-tag ${u.activo ? 'gu-tag--active' : 'gu-tag--inactive'}`}>
                          {t(u.activo ? 'frontlineUsers.statusActive' : 'frontlineUsers.statusInactive')}
                        </span>
                      </td>
                      <td data-label={t('frontlineUsers.colAction')} className="gu-c">
                        <button type="button" className="gu-icon-btn"
                          title={t('common.edit')}
                          aria-label={t('frontlineUsers.editLabel', { name: u.nombre })}
                          onClick={() => setModal({ kind: 'edit', user: u })}>
                          <i className="fas fa-pen" />
                        </button>
                      </td>
                      <td data-label={t('frontlineUsers.colSend')} className="gu-c">
                        <button type="button" className="gu-send-btn" disabled={sendDisabled || send.isPending}
                          title={sendTitle} onClick={() => void onSend(u)}>
                          <i className="fas fa-paper-plane" aria-hidden="true" />
                          {u.estado === 'Pendiente' ? t('frontlineUsers.resend') : t('frontlineUsers.send')}
                        </button>
                      </td>
                      <td data-label={t('frontlineUsers.colState')} className="gu-c">
                        <button type="button" className={`gu-state gu-st--${u.estado}`}
                          aria-label={t('frontlineUsers.verifLabel', { name: u.nombre })}
                          onClick={() => setModal({ kind: 'verification', user: u })}>
                          {t(`frontlineUsers.state.${u.estado}`)}
                        </button>
                        {sentOn && <span className="gu-sent-on">{sentOn}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </StateBlock>

        <div className="adm-pg-footer">
          <span className="adm-pg-info">{tr('admin.pager.showing', { range: total ? `${start + 1}–${end}` : '0', total })}</span>
          <div className="adm-pg-controls">
            {pages > 1 && (
              <>
                <button type="button" className="adm-pg-btn" disabled={safePage <= 1}
                  onClick={() => setPage(safePage - 1)} aria-label={tr('admin.pager.prev')}>‹</button>
                {pageNumbers(safePage, pages).map(p => (
                  <button key={p} type="button" aria-current={p === safePage ? 'page' : undefined}
                    className={`adm-pg-btn ${p === safePage ? 'adm-pg-btn--on' : ''}`} onClick={() => setPage(p)}>{p}</button>
                ))}
                <button type="button" className="adm-pg-btn" disabled={safePage >= pages}
                  onClick={() => setPage(safePage + 1)} aria-label={tr('admin.pager.next')}>›</button>
              </>
            )}
            <select className="adm-pg-size" value={pageSize} aria-label={tr('admin.pager.pageSize')}
              onChange={e => setPageSize(Number(e.target.value))}>
              {PAGE_SIZES.map(s => <option key={s} value={s}>{tr('admin.pager.perPage', { size: s })}</option>)}
            </select>
          </div>
        </div>
      </div>

      {modal?.kind === 'edit' && currentUser && (
        <EditEmailModal user={currentUser} onClose={() => setModal(null)} onSaved={showSuccess} onError={showError} />
      )}
      {modal?.kind === 'verification' && currentUser && (
        <VerificationModal user={currentUser} onClose={() => setModal(null)} onDone={showSuccess} onError={showError} />
      )}
      <Toast toast={toast} />
    </AdminSection>
    {canLoadPadron && (
      <div className="adm-gestion-loose-block">
        <AdminBulkUsers />
      </div>
    )}
    </>
  );
}

/** Hasta 5 números de página centrados en la actual (igual que el pie de paginación del resto del admin). */
function pageNumbers(current: number, pages: number): number[] {
  const maxShow = 5;
  let first = Math.max(1, current - Math.floor(maxShow / 2));
  const last = Math.min(pages, first + maxShow - 1);
  if (last - first < maxShow - 1) first = Math.max(1, last - maxShow + 1);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}
